import React, { useState, useEffect } from "react";
import { FaCircleCheck } from "react-icons/fa6";

const CRMPricingSlider = ({ tiers, onPriceChange }) => {
  const [customerCount, setCustomerCount] = useState(99);
  const [currentTier, setCurrentTier] = useState(null);

  // Find the appropriate tier based on customer count
  useEffect(() => {
    const tier = tiers.find((tier, index) => {
      if (index === 0) {
        return customerCount <= tier.up_to;
      }
      const prevTier = tiers[index - 1];
      if (tier.up_to === null) {
        // Last tier (50K+) - handle enterprise pricing
        return customerCount > prevTier.up_to;
      }
      return customerCount > prevTier.up_to && customerCount <= tier.up_to;
    });

    if (tier) {
      setCurrentTier(tier);
      onPriceChange(tier);
    }
  }, [customerCount, tiers, onPriceChange]);

  const handleSliderChange = (e) => {
    setCustomerCount(parseInt(e.target.value));
  };

  const formatCustomerCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toLocaleString();
  };

  const getSliderValue = (count) => {
    // Simple linear mapping based on actual customer count
    const maxCustomers =
      tiers.length > 0 ? (tiers[tiers.length - 2]?.up_to || 99999) + 1 : 20000;
    return Math.min((count / maxCustomers) * 100, 100);
  };

  const getCustomerCountFromSlider = (sliderValue) => {
    // Convert slider value back to customer count
    const maxCustomers =
      tiers.length > 0 ? (tiers[tiers.length - 2]?.up_to || 99999) + 1 : 20000;
    return Math.round((sliderValue / 100) * maxCustomers);
  };

  return (
    <div className="space-y-6">
      {/* Price Display */}
      <div className="text-center">
        <div className="text-3xl font-bold text-indigo-600 mb-2">
          {currentTier && currentTier.flat_amount
            ? `$${(currentTier.flat_amount / 100).toFixed(2)}`
            : currentTier
            ? "Contact Us"
            : "$0.00"}
        </div>
        <div className="text-sm text-slate-600">
          {currentTier && currentTier.flat_amount
            ? "per month"
            : "for custom pricing"}
        </div>
      </div>
      {currentTier && (
        <div className="bg-indigo-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-indigo-900">
                {currentTier.up_to
                  ? `Up to ${formatCustomerCount(currentTier.up_to)} customers`
                  : "20,000+ customers"}
              </div>
            </div>
            <FaCircleCheck className="text-indigo-600 text-xl flex-shrink-0" />
          </div>
        </div>
      )}
      {/* Slider */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-slate-700">
            Customer list size
          </span>
          <span className="text-lg font-bold text-indigo-600">
            {formatCustomerCount(customerCount)} customers
          </span>
        </div>

        <div className="relative">
          <input
            type="range"
            min="0"
            max="100"
            value={getSliderValue(customerCount)}
            onChange={(e) =>
              setCustomerCount(getCustomerCountFromSlider(e.target.value))
            }
            className="w-full h-2 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${getSliderValue(
                customerCount
              )}%, #e2e8f0 ${getSliderValue(customerCount)}%, #e2e8f0 100%)`,
            }}
          />
        </div>
      </div>

      {/* Current Tier Info */}
    </div>
  );
};

export default CRMPricingSlider;
